/**
 * Shared Auth Endpoint Builders
 *
 * These endpoints are used by both UI and SI applications for authentication.
 *
 * ⚠️ IMPORTANT - Multi-App Cookie Isolation:
 * Auth routes use app-specific paths for automatic cookie isolation.
 *
 * UI App (appContext: 'ui'):
 * - /auth/ui/refresh           - Reads refresh_token cookie (path=/auth/ui/refresh)
 * - /auth/ui/store-refresh-token - Sets refresh_token cookie (path=/auth/ui/refresh)
 * - /auth/ui/csrf-token        - Sets csrf_token cookie (path=/)
 * - /auth/ui/clear-cookies     - Clears cookies (path=/auth/ui/refresh)
 *
 * SI App (appContext: 'si'):
 * - /auth/si/refresh           - Reads refresh_token cookie (path=/auth/si/refresh)
 * - /auth/si/store-refresh-token - Sets refresh_token cookie (path=/auth/si/refresh)
 * - /auth/si/csrf-token        - Sets csrf_token cookie (path=/)
 * - /auth/si/clear-cookies     - Clears cookies (path=/auth/si/refresh)
 *
 * Security Model (RFC 6265 compliant):
 * - Refresh token cookies are ONLY sent to the refresh endpoint (path=/auth/{app}/refresh)
 * - This follows the principle of least privilege - tokens only exposed where needed
 * - CSRF tokens are sent to all endpoints (path=/) for CSRF protection
 * - Cookie names provide app isolation (emalino_refresh_token vs emalino_si_refresh_token)
 *
 * ⚠️ IMPORTANT - Authenticated User Routes:
 * Routes requiring authentication use /user/* prefix to avoid conflicts with
 * Laravel's built-in 'auth' middleware alias.
 */

/**
 * API version constants
 */
export const API_VERSION = {
  DEFAULT: 'v1',
  AUTH: 'v1',
} as const;

/**
 * Get the versioned path for an endpoint
 */
export function versionedPath(
  path: string,
  version: string = API_VERSION.DEFAULT
): string {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;

  // If no version (empty string), return path as-is
  if (!version) {
    return `/${cleanPath}`;
  }

  // Add version prefix
  return `/${version}/${cleanPath}`;
}

/**
 * Auth endpoint builders
 */
export const authEndpoints = {
  /** Login endpoint - browser-safe OAuth flow (non-versioned for consistency with other auth routes) */
  login: () => versionedPath('auth/login', ''),

  /** OAuth/Passport token endpoint - ONLY for service clients (blocked for browser clients) */
  token: () => versionedPath('oauth/token', ''),

  /** Refresh token endpoint - app-specific path (NO versioning for cookie path compatibility) */
  refresh: (appContext: 'ui' | 'si' = 'ui') =>
    versionedPath(`auth/${appContext}/refresh`, ''),

  /** Logout endpoint (renamed to avoid middleware conflict) */
  logout: () => versionedPath('user/logout', API_VERSION.AUTH),

  /** Logout all devices endpoint */
  logoutAllDevices: () =>
    versionedPath('user/logout-all-devices', API_VERSION.AUTH),

  /** CSRF token endpoint - app-specific path for cookie isolation */
  csrfToken: (appContext: 'ui' | 'si' = 'ui') =>
    versionedPath(`auth/${appContext}/csrf-token`, ''),

  /** Clear cookies endpoint - app-specific path for cookie isolation */
  clearCookies: (appContext: 'ui' | 'si' = 'ui') =>
    versionedPath(`auth/${appContext}/clear-cookies`, ''),

  /** User sessions endpoint */
  sessions: () => versionedPath('user/sessions', API_VERSION.AUTH),

  /** Revoke specific session */
  revokeSession: (sessionId: string) =>
    versionedPath(`user/sessions/${sessionId}`, API_VERSION.AUTH),
};

/**
 * Account endpoint builders (for user profile)
 */
export const accountEndpoints = {
  /** Base account endpoint */
  base: () => versionedPath('account', API_VERSION.DEFAULT),

  /** Account section endpoint */
  section: (section: string) =>
    versionedPath(`account/${section}`, API_VERSION.DEFAULT),
};
